import { DocsCallout } from "@/presentation/components/docs/docs-callout";
import { DocsCodeBlock } from "@/presentation/components/docs/docs-code-block";
import { DocsRichTextView } from "@/presentation/components/docs/docs-rich-text";
import { MermaidDiagram } from "@/presentation/components/diagrams/mermaid-diagram";
import type { DocsBlock } from "@/presentation/data/docs/docs-content.types";

interface DocsBlockViewProps {
  block: DocsBlock;
}

/** Maps a single content block to its rendered element. */
export function DocsBlockView({ block }: DocsBlockViewProps) {
  switch (block.kind) {
    case "heading":
      return (
        <h2
          id={block.id}
          className="text-foreground mt-10 scroll-mt-24 border-b border-[var(--border)] pb-1.5 text-lg font-semibold tracking-tight first:mt-0"
        >
          {block.text}
        </h2>
      );
    case "paragraph":
      return (
        <p className="text-muted-foreground my-3 text-sm leading-relaxed">
          <DocsRichTextView value={block.text} />
        </p>
      );
    case "list":
      return <DocsListView block={block} />;
    case "code":
      return <DocsCodeBlock block={block} />;
    case "mermaid":
      return (
        <div className="my-4">
          <MermaidDiagram
            chart={block.chart}
            eyebrow={block.eyebrow}
            title={block.title}
            caption={block.caption}
            fallbackHeight={block.fallbackHeight}
          />
        </div>
      );
    case "callout":
      return <DocsCallout block={block} />;
    case "table":
      return <DocsTableView block={block} />;
    case "key-value":
      return <DocsKeyValueView block={block} />;
  }
}

function DocsListView({ block }: { block: Extract<DocsBlock, { kind: "list" }> }) {
  const className = "text-muted-foreground my-3 ml-5 space-y-1.5 text-sm leading-relaxed";
  const itemMarker = block.ordered ? "list-decimal" : "list-disc";
  const items = block.items.map((item, i) => (
    <li key={i} className="pl-1">
      <DocsRichTextView value={item} />
    </li>
  ));
  return block.ordered ? (
    <ol className={`${className} ${itemMarker}`}>{items}</ol>
  ) : (
    <ul className={`${className} ${itemMarker}`}>{items}</ul>
  );
}

function DocsTableView({ block }: { block: Extract<DocsBlock, { kind: "table" }> }) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-[var(--border)]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/60">
            {block.headers.map((h, i) => (
              <th
                key={i}
                className="text-foreground border-b border-[var(--border)] px-3 py-2 text-left font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r} className="border-b border-[var(--border)] last:border-0">
              {row.map((cell, c) => (
                <td key={c} className="text-muted-foreground px-3 py-2 align-top leading-relaxed">
                  <DocsRichTextView value={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocsKeyValueView({ block }: { block: Extract<DocsBlock, { kind: "key-value" }> }) {
  return (
    <dl className="my-4 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
      {block.pairs.map((pair, i) => (
        <div key={i} className="contents">
          <dt className="text-foreground font-mono text-xs">{pair.key}</dt>
          <dd className="text-muted-foreground leading-relaxed">
            <DocsRichTextView value={pair.value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
